import React, { useState, useEffect } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Staff = () => {
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | ACTIVE | INACTIVE
  const [formData, setFormData] = useState({
    fullname: "",
    phone: "",
    email: "",
    password: "",
  });

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}admin/staff`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.status === "success") {
        setStaff(data.data || []);
      } else {
        setError("Failed to fetch staff");
      }
    } catch (err) {
      setError("Error fetching staff");
      console.error("Staff fetch error:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}admin/staff`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.status === "success") {
        setSuccess("Staff member added successfully!");
        setShowModal(false);
        fetchStaff();
        setFormData({
          fullname: "",
          phone: "",
          email: "",
          password: "",
        });
      } else {
        setError(data.message || "Failed to add staff member");
      }
    } catch (err) {
      setError("Error adding staff member");
      console.error("Staff creation error:", err);
    }
    setLoading(false);
  };

  const toggleStaffStatus = async (staffId, currentStatus) => {
    try {
      const response = await fetch(`${BASE_URL}admin/staff/${staffId}/toggle`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ isActive: !currentStatus }),
      });
      const data = await response.json();
      if (data.status === "success") {
        setSuccess(
          `Staff status ${currentStatus ? "deactivated" : "activated"} successfully!`
        );
        fetchStaff();
      } else {
        setError(data.message || "Failed to update staff status");
      }
    } catch (err) {
      setError("Error updating staff status");
      console.error("Staff status update error:", err);
    }
  };

  const filteredStaff = staff.filter((member) => {
    const matchesFilter =
      statusFilter === "ALL" ||
      (statusFilter === "ACTIVE" && member.isActive) ||
      (statusFilter === "INACTIVE" && !member.isActive);

    const query = searchQuery.toLowerCase().trim();
    const matchesQuery =
      !query ||
      (member.fullname && member.fullname.toLowerCase().includes(query)) ||
      (member.email && member.email.toLowerCase().includes(query)) ||
      (member.phone && member.phone.includes(query));

    return matchesFilter && matchesQuery;
  });

  return (
    <div className="min-h-screen bg-stone-50/70 text-stone-900 flex">
      <Sidebar admin={admin} onLogout={handleLogout} />

      <main className="lg:ml-72 min-h-screen flex-1 pb-16">
        {/* Modern Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-stone-200/80 sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-4 lg:py-6 shadow-xs">
          <div className="pt-16 lg:pt-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">
                <span>Admin</span>
                <span>/</span>
                <span>Management</span>
                <span>/</span>
                <span className="text-stone-900 font-bold">Staff Directory</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-stone-900 tracking-tight flex items-center gap-3">
                <span>Staff Management</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-stone-100 text-stone-700 border border-stone-200">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span>{staff.length} Members</span>
                </span>
              </h1>
              <p className="text-sm text-stone-500 mt-1">
                Manage your operational staff members, dispatch access, and admin credentials.
              </p>
            </div>

            <div className="flex items-center gap-3 self-start sm:self-auto">
              <button
                onClick={() => fetchStaff()}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-white hover:bg-stone-50 border border-stone-200 text-stone-700 rounded-xl text-xs font-semibold transition-all shadow-xs disabled:opacity-60"
                title="Refresh Staff List"
              >
                <svg
                  className={`w-4 h-4 text-stone-600 ${loading ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span className="hidden sm:inline">Refresh</span>
              </button>

              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-4 sm:px-5 py-2.5 rounded-xl font-semibold text-xs transition-all shadow-xs transform hover:scale-[1.02]"
              >
                <span className="w-5 h-5 rounded-lg bg-[#F8D346] text-stone-900 flex items-center justify-center font-bold text-sm leading-none">
                  +
                </span>
                <span>Add Staff Member</span>
              </button>
            </div>
          </div>
        </header>

        {/* Alert Messages */}
        <div className="px-4 sm:px-6 lg:px-8 pt-4">
          {error && (
            <div className="mb-4 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-medium flex items-center justify-between shadow-xs">
              <div className="flex items-center gap-2.5">
                <svg
                  className="w-5 h-5 text-rose-600 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
              <button
                onClick={() => setError("")}
                className="text-rose-500 hover:text-rose-700 text-xs font-bold"
              >
                Dismiss
              </button>
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium flex items-center justify-between shadow-xs">
              <div className="flex items-center gap-2.5">
                <svg
                  className="w-5 h-5 text-emerald-600 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{success}</span>
              </div>
              <button
                onClick={() => setSuccess("")}
                className="text-emerald-500 hover:text-emerald-700 text-xs font-bold"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>

        {/* Content Body */}
        <div className="px-4 sm:px-6 lg:px-8 pt-2">
          {/* KPI Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {/* Total Staff */}
            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-stone-200/80 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  Total Staff
                </span>
                <span className="w-8 h-8 rounded-xl bg-stone-100 flex items-center justify-center text-stone-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </span>
              </div>
              <div className="text-2xl lg:text-3xl font-bold text-stone-900 mt-2">
                {staff.length}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Authorized team accounts
              </div>
            </div>

            {/* Active Staff */}
            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-stone-200/80 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                  Active Members
                </span>
                <span className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                  ✓
                </span>
              </div>
              <div className="text-2xl lg:text-3xl font-bold text-emerald-700 mt-2">
                {staff.filter((s) => s.isActive).length}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Currently enabled access
              </div>
            </div>

            {/* Inactive Staff */}
            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-stone-200/80 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-rose-700">
                  Suspended / Inactive
                </span>
                <span className="w-8 h-8 rounded-xl bg-rose-50 text-rose-700 flex items-center justify-center font-bold">
                  ✕
                </span>
              </div>
              <div className="text-2xl lg:text-3xl font-bold text-rose-700 mt-2">
                {staff.filter((s) => !s.isActive).length}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Access revoked / paused
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="bg-white rounded-2xl p-3 sm:p-4 border border-stone-200/80 shadow-xs mb-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Search input */}
            <div className="relative w-full sm:w-80">
              <svg
                className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by name, phone or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2 bg-stone-50 border border-stone-200 rounded-xl text-xs font-medium text-stone-800 placeholder-stone-400 focus:bg-white focus:ring-2 focus:ring-[#F8D346] focus:border-stone-400 transition-all outline-none"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Filter Pills */}
            <div className="flex items-center gap-1.5 p-1 bg-stone-100 rounded-xl w-full sm:w-auto self-stretch sm:self-auto justify-center">
              {["ALL", "ACTIVE", "INACTIVE"].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setStatusFilter(filter)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    statusFilter === filter
                      ? "bg-white text-stone-900 shadow-xs border border-stone-200/80"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  {filter === "ALL"
                    ? `All (${staff.length})`
                    : filter === "ACTIVE"
                    ? `Active (${staff.filter((s) => s.isActive).length})`
                    : `Inactive (${staff.filter((s) => !s.isActive).length})`}
                </button>
              ))}
            </div>
          </div>

          {/* Staff Grid or Empty State */}
          {loading ? (
            <div className="bg-white rounded-2xl border border-stone-200/80 p-12 text-center shadow-xs">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-stone-200 border-t-stone-900 mb-3" />
              <p className="text-sm font-semibold text-stone-600">
                Loading staff records...
              </p>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200/80 p-12 text-center shadow-xs">
              <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4 text-stone-400">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1">
                {searchQuery ? "No matching staff found" : "No Staff Members Registered"}
              </h3>
              <p className="text-xs text-stone-500 max-w-sm mx-auto mb-5">
                {searchQuery
                  ? "Try changing your search query or switching status filters."
                  : "Get started by adding staff members to handle daily operations and dispatch."}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all shadow-xs"
                >
                  <span>+ Add First Staff Member</span>
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredStaff.map((member) => (
                <div
                  key={member.id}
                  className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-xs hover:border-stone-300 hover:shadow-sm transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 via-[#F8D346] to-amber-500 text-stone-900 font-bold text-base flex items-center justify-center shadow-xs shrink-0">
                          {member.fullname
                            ? member.fullname
                                .split(" ")
                                .map((n) => n[0])
                                .slice(0, 2)
                                .join("")
                                .toUpperCase()
                            : "ST"}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-stone-900 text-sm truncate">
                            {member.fullname || "Unnamed Staff"}
                          </h3>
                          <p className="text-xs text-stone-500 truncate font-mono">
                            {member.email || "No email"}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => toggleStaffStatus(member.id, member.isActive)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-all border shrink-0 ${
                          member.isActive
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200/80 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200"
                            : "bg-rose-50 text-rose-700 border-rose-200/80 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200"
                        }`}
                        title="Click to toggle status"
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            member.isActive ? "bg-emerald-500" : "bg-rose-500"
                          }`}
                        />
                        <span>{member.isActive ? "Active" : "Inactive"}</span>
                      </button>
                    </div>

                    <div className="space-y-2 pt-3 border-t border-stone-100 text-xs text-stone-600">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-3.5 h-3.5 text-stone-400 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span className="font-mono text-stone-700">
                          {member.phone || "No phone registered"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <svg
                          className="w-3.5 h-3.5 text-stone-400 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span>
                          Joined:{" "}
                          {member.createdAt
                            ? new Date(member.createdAt).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                            : "Recently"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                    <span className="text-stone-400 font-mono">
                      ID: #{member.id.slice(-6).toUpperCase()}
                    </span>
                    <span className="text-stone-500 font-medium">
                      {member.isActive ? "Access enabled" : "Access suspended"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Staff Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between pb-4 border-b border-stone-100 mb-5">
                <div>
                  <h2 className="text-lg font-bold text-stone-900">
                    Add New Staff Member
                  </h2>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Create credentials for admin panel operations
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 rounded-lg bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-500 hover:text-stone-800 transition-colors"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Full Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.fullname}
                    onChange={(e) =>
                      setFormData({ ...formData, fullname: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-[#F8D346] focus:border-stone-400 text-xs font-medium text-stone-800 transition-all outline-none"
                    placeholder="e.g. Rahul Sharma"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Phone Number <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-[#F8D346] focus:border-stone-400 text-xs font-medium text-stone-800 transition-all outline-none font-mono"
                    placeholder="e.g. 9876543210"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-[#F8D346] focus:border-stone-400 text-xs font-medium text-stone-800 transition-all outline-none"
                    placeholder="e.g. rahul@ineedbuddy.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                    Password <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-[#F8D346] focus:border-stone-400 text-xs font-medium text-stone-800 transition-all outline-none"
                    placeholder="Enter login password"
                  />
                </div>

                <div className="flex gap-3 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-semibold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2.5 bg-stone-900 hover:bg-stone-800 text-white rounded-xl text-xs font-semibold transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Adding..." : "Add Staff Member"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Staff;
